<template>
    <div class="intent--actions">
        <div class="custom-icons--wrapper">
            <entity-icon />
            <feature-icon />
        </div>
        <div class="btns-wrap">
            <div
                v-if="isIntentUpdating"
                class="loader-wrap"
            >
                <or-icon icon="sync" />
            </div>
            <div
                v-else
                class="active-features--wrapper"
            >
                <or-icon-button
                    v-if="appliedFunctionalityOptions.length !== 0"
                    ref="appliedFunctionalityButton"
                    icon="info"
                    size="small"
                    type="secondary"
                    class="flat"
                    has-dropdown
                    disableRipple
                >
                    <or-menu
                        class="applied-functionality--dropdown"
                        slot="dropdown"
                        contain-focus
                        has-icons
                        disable-ripple
                        :options="appliedFunctionalityOptions"
                        @select="selectMenuHandler"
                        @close="$refs.appliedFunctionalityButton.closeDropdown()"
                    />
                </or-icon-button>
                <or-icon-button
                    ref="dropdownButton"
                    icon="more_vert"
                    size="small"
                    type="secondary"
                    class="flat"
                    has-dropdown
                    disableRipple
                >
                    <or-menu
                        slot="dropdown"
                        contain-focus
                        has-icons
                        disable-ripple
                        :options="menuOptions"
                        @select="selectMenuHandler"
                        @close="$refs.dropdownButton.closeDropdown()"
                    />
                </or-icon-button>
            </div>
        </div>
        <feature-modal
            :opened="showFeaturesModal"
            :new-features="intentFeaturesAndEntities.length === 0"
            :selected-features="intentFeaturesAndEntities"
            @update-features="updateIntentFeatures"
            @close="showFeaturesModal = false"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import FeatureIcon from '@/assets/feature-icon'
import EntityIcon from '@/assets/entity-icon'
import FeatureModal from '../modals/feature_modal'

export default {
  components: {
    FeatureModal,
    EntityIcon,
    FeatureIcon
  },

  props: {
    intent: Object,
    isNewIntent: Boolean,
    hasNoFeatures: Boolean,
    areFeaturesEnabled: Boolean,
    isIntentUpdating: Boolean,
    newConfirmationPhrase: {
      type: Boolean,
      default: () => true
    }
  },

  data () {
    return {
      showFeaturesModal: false
    }
  },

  computed: {
    appliedFunctionalityOptions () {
      const appliedFunctionality = []
      if (!this.$props.newConfirmationPhrase) {
        appliedFunctionality.push({
          id: 'edit-confirmation-phrase',
          label: this.$t('generic.confirmationPhrase'),
          icon: 'speaker_notes'
        })
      }

      if (this.intentFeatures.length !== 0) {
        appliedFunctionality.push({
          id: 'add-feature',
          label: this.$t('generic.features'),
          icon: 'feature-icon',
          // Not required to pass icon, simply avoid runtime warning
          iconProps: {
            useSvg: true,
            icon: 'feature-icon'
          }
        })
      }

      if (this.intentEntities.length !== 0) {
        appliedFunctionality.push({
          id: 'add-feature',
          label: this.$t('generic.entities'),
          icon: 'entity-icon',
          // Not required to pass icon, simply avoid runtime warning
          iconProps: {
            useSvg: true,
            icon: 'entity-icon'
          }
        })
      }

      // TODO: no sure regarding correct type & edge cases, maybe it can be shorten to .length !== 0
      if (this.$props.intent && this.$props.intent.postprocessFlows && this.$props.intent.postprocessFlows.length !== 0) {
        appliedFunctionality.push({
          id: 'add-flow',
          label: this.$t('common.intent.intent_actions.flowEntity.label'),
          icon: 'swap_calls'
        })
      }

      if (appliedFunctionality.length !== 0) {
        appliedFunctionality.unshift({
          id: 'header',
          label: this.$t('common.intent.intent_actions.appliedFunctionality'),
          disabled: true
        })
      }

      return appliedFunctionality
    },

    menuOptions () {
      const hasFeatures = this.intentFeatures.length !== 0 || this.intentEntities.length !== 0
      const feature = {
        id: 'add-feature',
        label: this.$t(`common.intent.intent_actions.featuresAndEntities.${hasFeatures ? 'edit' : 'add'}`),
        icon: hasFeatures ? 'edit' : 'add'
      }

      const confirmationPhrase = {
        id: 'edit-confirmation-phrase',
        label: this.$t(`common.intent.intent_actions.confirmationPhrase.${this.$props.newConfirmationPhrase ? 'add' : 'edit'}`),
        icon: this.$props.newConfirmationPhrase ? 'add' : 'edit'
      }

      const deletePhrase = {
        id: 'delete',
        label: this.$t('common.intent.intent_actions.deleteResponse'),
        icon: 'delete-intent'
      }

      const hasFlows = this.$props.intent && this.$props.intent.postprocessFlows && this.$props.intent.postprocessFlows.length !== 0

      const flowEntity = {
        id: 'add-flow',
        label: this.$t(`common.intent.intent_actions.flowEntity.${hasFlows ? 'edit' : 'add'}`),
        icon: hasFlows ? 'edit' : 'add'
      }

      const options = this.$props.hasNoFeatures
        ? []
        : [confirmationPhrase, flowEntity, deletePhrase]

      if (this.areFeaturesEnabled) {
        options.unshift(feature)
      }

      if (!this.$props.isNewIntent) {
        options.push({
          id: 'edit-intent',
          label: this.$t('common.intent.intent_actions.response.edit'),
          icon: 'edit'
        })
      }

      return options
    },

    intentFeaturesAndEntities () {
      // features can be undefined
      return (this.$props.intent && this.$props.intent.features) || []
    },

    intentFeatures () {
      return this.intentFeaturesAndEntities.filter(modelOrFeature => _.has(modelOrFeature, 'featureName'))
    },

    intentEntities () {
      return this.intentFeaturesAndEntities.filter(modelOrFeature => _.has(modelOrFeature, 'modelName'))
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['updateIntent']),

    updateIntentFeatures (features) {
      this.updateIntent({
        ...this.$props.intent,
        features
      })
      this.showFeaturesModal = false
    },

    selectMenuHandler (action) {
      switch (action.id) {
        case ('add-feature'): {
          this.showFeaturesModal = true
          break
        }
        default:
          this.$emit(action.id)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
    .btns-wrap {
        display: flex;
        .active-features--wrapper {
            display: flex;
        }

        .loader-wrap {
            display: flex;
            width: 32px;
            height: 30px;
            align-items: center;
            justify-content: center;

            .material-icons {
                font-size: 18px;
                color: $or-blue;
                border-radius: 100%;

                @keyframes spin { 100% { transform: rotate(-360deg); } }
                animation: spin 1s linear infinite;

                &:hover { cursor: default; }
            }
        }
    }
    .custom-icons--wrapper {
        height: 0;
        width: 0;
        overflow: hidden;
    }
    .applied-functionality--dropdown {
        ::v-deep {
            .menu-item {
                min-height: 0;
            }
            .ui-menu-option__content {
                height: auto;
                padding: $sp-2;
            }
            .ui-menu-option__icon {
                margin-right: $sp-2;
            }
            .feature-icon, .entity-icon {
                padding-left: 3px;
            }
            .feature-icon {
                .ui-icon__svg {
                    height: 14px;
                    width: 14px;
                }
            }
            .entity-icon {
                .ui-icon__svg {
                    height: 16px;
                    width: 14px;
                }
            }
        }
    }
</style>
